/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState } from 'react';
import {
  getConsultationOutcomes,
  IConsultationOutcome,
} from '../promises/getConsultationOutcomes';
import {
  getConsultationTypes,
  IConsultationType,
} from '../promises/getConsultationTypes';
import {
  getModeOfContacts,
  IModeOfContact,
} from '../promises/getModeOfContacts';
import {
  getReferralSources,
  IReferralSource,
} from '../promises/getReferralSources';
import useApiResult from './useApiResult';
import {
  getPatientAgeRanges,
  IPatientAgeRange,
} from '../promises/getPatientAgeRanges';
import {
  getDoubleAppointmentReasons,
  IDoubleAppointmentReason,
} from '../promises/getDoubleAppointmentReasons';
import {
  getFollowUpMethods,
  IFollowUpMethod,
} from '../promises/getFollowUpMethods';
import {
  getFollowUpReasons,
  IFollowUpReason,
} from '../promises/getFollowUpReasons';
import {
  getNextAvailableAppointments,
  INextAvailableAppointment,
} from '../promises/getNextAvailableAppointments';

export interface ILookupTypes {
  consultationOutcomes: IConsultationOutcome[];
  consultationTypes: IConsultationType[];
  modeOfContacts: IModeOfContact[];
  referralSources: IReferralSource[];
  patientAgeRanges: IPatientAgeRange[];
  doubleAppointmentReasons: IDoubleAppointmentReason[];
  followUpMethods: IFollowUpMethod[];
  followUpReasons: IFollowUpReason[];
  nextAvailableAppointments: INextAvailableAppointment[];
}

export function useGetLookupTypes() {
  const [result, setResult] = useState<ILookupTypes>({
    consultationOutcomes: [],
    consultationTypes: [],
    modeOfContacts: [],
    referralSources: [],
    patientAgeRanges: [],
    doubleAppointmentReasons: [],
    followUpMethods: [],
    followUpReasons: [],
    nextAvailableAppointments: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const consultationOutcomesResult = useMemo(
    () => getConsultationOutcomes(),
    []
  );
  const {
    loading: consultationOutcomesLoading,
    error: consultationOutcomesError,
    result: consultationOutcomesData,
  } = useApiResult(consultationOutcomesResult);

  const consultationTypesResult = useMemo(() => getConsultationTypes(), []);
  const {
    loading: consultationTypesLoading,
    error: consultationTypesError,
    result: consultationTypesData,
  } = useApiResult(consultationTypesResult);

  const modeOfContactsResult = useMemo(() => getModeOfContacts(), []);
  const {
    loading: modeOfContactsLoading,
    error: modeOfContactsError,
    result: modeOfContactsData,
  } = useApiResult(modeOfContactsResult);

  const referralSourcesResult = useMemo(() => getReferralSources(), []);
  const {
    loading: referralSourcesLoading,
    error: referralSourcesError,
    result: referralSourcesData,
  } = useApiResult(referralSourcesResult);

  const patientAgeRangesResult = useMemo(() => getPatientAgeRanges(), []);
  const {
    loading: patientAgeRangesLoading,
    error: patientAgeRangesError,
    result: patientAgeRangesData,
  } = useApiResult(patientAgeRangesResult);

  const doubleAppointmentReasonsResult = useMemo(
    () => getDoubleAppointmentReasons(),
    []
  );
  const {
    loading: doubleAppointmentReasonsLoading,
    error: doubleAppointmentReasonsError,
    result: doubleAppointmentReasonsData,
  } = useApiResult(doubleAppointmentReasonsResult);

  const followUpMehodsResult = useMemo(() => getFollowUpMethods(), []);
  const {
    loading: followUpMethodsLoading,
    error: followUpMethodsError,
    result: followUpMethodsData,
  } = useApiResult(followUpMehodsResult);

  const followUpReasonsResult = useMemo(() => getFollowUpReasons(), []);
  const {
    loading: followUpReasonsLoading,
    error: followUpReasonsError,
    result: followUpReasonsData,
  } = useApiResult(followUpReasonsResult);

  const nextAvailableAppointmentsResult = useMemo(
    () => getNextAvailableAppointments(),
    []
  );
  const {
    loading: nextAvailableAppointmentsLoading,
    error: nextAvailableAppointmentsError,
    result: nextAvailableAppointmentsData,
  } = useApiResult(nextAvailableAppointmentsResult);

  React.useEffect(() => {
    setLoading(
      consultationOutcomesLoading ||
        consultationTypesLoading ||
        modeOfContactsLoading ||
        referralSourcesLoading ||
        patientAgeRangesLoading ||
        doubleAppointmentReasonsLoading ||
        followUpMethodsLoading ||
        followUpReasonsLoading ||
        nextAvailableAppointmentsLoading
    );

    setResult({
      consultationOutcomes: consultationOutcomesData ?? [],
      consultationTypes: consultationTypesData ?? [],
      modeOfContacts: modeOfContactsData ?? [],
      referralSources: referralSourcesData ?? [],
      patientAgeRanges: patientAgeRangesData ?? [],
      doubleAppointmentReasons: doubleAppointmentReasonsData ?? [],
      followUpMethods: followUpMethodsData ?? [],
      followUpReasons: followUpReasonsData ?? [],
      nextAvailableAppointments: nextAvailableAppointmentsData ?? [],
    });
  }, [
    consultationOutcomesLoading,
    consultationTypesLoading,
    modeOfContactsLoading,
    referralSourcesLoading,
    patientAgeRangesLoading,
    nextAvailableAppointmentsLoading,
  ]);

  React.useEffect(() => {
    setError(
      consultationOutcomesError ||
        consultationTypesError ||
        modeOfContactsError ||
        referralSourcesError ||
        patientAgeRangesError ||
        doubleAppointmentReasonsError ||
        followUpMethodsError ||
        followUpReasonsError ||
        nextAvailableAppointmentsError
    );
  }, [
    consultationOutcomesError,
    consultationTypesError,
    modeOfContactsError,
    referralSourcesError,
    patientAgeRangesError,
    doubleAppointmentReasonsError,
    nextAvailableAppointmentsError,
  ]);

  return { loading, error, result };
}
